import { useMemo, useState } from 'react';
import {
  useInfiniteQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import { useStore } from '@frodo/lib/hooks/useStore';
import { reviewsInfiniteQuery } from '@frodo/lib/services/http/client/api-reviews.client';
import { REVIEWS_PAGE_SIZE } from '@frodo/lib/utils/common/constants';
import type {
  ReviewFilterApplyParams,
  ReviewSortOption,
  ReviewsResponse,
} from '@frodo/models/types';

const DEFAULT_FILTERS: ReviewFilterApplyParams = {
  industryIds: [],
  sort: 'newest',
};

export type UseInternalReviewsOptions = {
  perPage?: number;
};

export const useInternalReviews = (
  productSku: string,
  { perPage = REVIEWS_PAGE_SIZE }: UseInternalReviewsOptions = {},
) => {
  const store = useStore();
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  const queryOptions = reviewsInfiniteQuery(
    productSku,
    store.country,
    filters,
    perPage,
  );

  const { data, isLoading, isFetching, hasNextPage, fetchNextPage } =
    useInfiniteQuery({
      ...queryOptions,
      placeholderData: previousData => previousData,
      retry: 2,
      retryDelay: attempt => Math.min(1000 * 2 ** attempt, 5000),
      throwOnError: false,
    });

  const pages = useMemo(
    () => data?.pages.map(page => page.reviews) ?? [],
    [data?.pages],
  );
  const summary = data?.pages[0];

  const reset = () => {
    queryClient.setQueryData<InfiniteData<ReviewsResponse, number>>(
      queryOptions.queryKey,
      current =>
        current && {
          pages: current.pages.slice(0, 1),
          pageParams: current.pageParams.slice(0, 1),
        },
    );
  };

  return {
    pages,
    filters,
    overallScores: summary?.overallScores,
    industries: summary?.industries ?? [],
    isLoading,
    isFetching,
    hasNextPage: Boolean(hasNextPage),
    fetchNextPage: () => {
      void fetchNextPage();
    },
    reset,
    updateSort: (sort: ReviewSortOption) => setFilters({ ...filters, sort }),
    applyReviewFilters: setFilters,
  };
};
